import { Directive, Input, TemplateRef, ViewContainerRef, OnInit, OnDestroy } from '@angular/core';
import { PermissionService } from '../permissions/permission.service';
import { UserRole } from '../user/user.types';
import { Subject, takeUntil } from 'rxjs';

/**
 * Has Role Directive
 * 
 * Structural directive to conditionally show/hide elements based on user role.
 * 
 * Usage:
 * ```html
 * <!-- Show only to Admin -->
 * <button *hasRole="'admin'">Admin Only</button>
 * 
 * <!-- Show to multiple roles -->
 * <button *hasRole="['admin', 'project_manager']">Admins & PMs</button>
 * 
 * <!-- Show to everyone except Reader -->
 * <button *hasRole="['admin', 'project_manager', 'writer']">Not for Reader</button>
 * ```
 */
@Directive({
    selector: '[hasRole]',
    standalone: true
})
export class HasRoleDirective implements OnInit, OnDestroy {
    private _roles: UserRole[] = [];
    private _destroy$ = new Subject<void>();

    @Input() set hasRole(roles: UserRole | UserRole[]) {
        this._roles = Array.isArray(roles) ? roles : [roles];
        this.updateView();
    }

    constructor(
        private templateRef: TemplateRef<any>,
        private viewContainer: ViewContainerRef,
        private permissionService: PermissionService
    ) {}

    ngOnInit(): void {
        this.updateView();
    }

    ngOnDestroy(): void {
        this._destroy$.next();
        this._destroy$.complete();
    }

    private updateView(): void {
        const hasPermission = this.permissionService.hasRole(this._roles);
        
        // Always clear first to prevent duplicates
        this.viewContainer.clear();
        
        if (hasPermission) {
            this.viewContainer.createEmbeddedView(this.templateRef);
        }
    }
}
